import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import supabase from '@/supabaseClient'
import EditGameModal from '@/components/EditGameModal/EditGameModal'
import { Game as BaseGame } from '@/types'

interface Game
  extends Omit<BaseGame, 'game_genres' | 'game_platforms' | 'game_moods'> {
  platforms: string[]
  genres: string[]
  image: string
  game_platforms?: { platforms: { name: string } }[]
}

interface GameCardItemProps {
  game: Game
  userId: string
  onRefresh: () => void
}

const toCoverImageUrl = (imageUrl?: string) => {
  if (!imageUrl) return '/default-image.jpg'
  if (!imageUrl.includes('images.igdb.com/igdb/image/upload')) return imageUrl
  if (imageUrl.includes('t_1080p')) return imageUrl
  return imageUrl
    .replace('/t_screenshot_big/', '/t_1080p/')
    .replace('/t_cover_big/', '/t_1080p/')
    .replace('/t_thumb/', '/t_1080p/')
}

const getStatusBadgeColor = (status: string): string => {
  switch (status) {
    case 'Endless':
    case 'Satisfied':
    case 'Done':
      return 'bg-green-100 text-green-800'
    case 'DNF':
      return 'bg-rose-100 text-rose-800'
    case 'Wishlist':
    case 'Try Again':
    case 'Started':
    case 'Owned':
      return 'bg-blue-100 text-blue-800'
    case 'Come back!':
    case 'Currently Playing':
      return 'bg-amber-100 text-amber-800'
    default:
      return 'bg-gray-100 text-gray-800'
  }
}

const GameCardItem = ({ game, userId, onRefresh }: GameCardItemProps) => {
  const navigate = useNavigate()
  const [showEditModal, setShowEditModal] = useState(false)
  const [isOverlayOpen, setIsOverlayOpen] = useState(false)
  const [isTouchDevice, setIsTouchDevice] = useState(false)
  const coverImage = toCoverImageUrl(game.image)

  useEffect(() => {
    if (typeof window === 'undefined') return
    const mediaQuery = window.matchMedia('(hover: none), (pointer: coarse)')
    const handleChange = () => setIsTouchDevice(mediaQuery.matches)
    handleChange()
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handleChange)
      return () => mediaQuery.removeEventListener('change', handleChange)
    }
    mediaQuery.addListener(handleChange)
    return () => mediaQuery.removeListener(handleChange)
  }, [])

  const handleDeleteClick = async (gameId: string) => {
    if (!confirm('Are you sure you want to delete this game?')) return

    const deleteSteps: [string, string, { eq: [string, string] }?][] = [
      ['game_moods', 'game_id'],
      ['game_platforms', 'game_id'],
      ['game_genres', 'game_id'],
      ['game_notes', 'game_id'],
      ['user_games', 'game_id', { eq: ['user_id', userId] }],
      ['games', 'id'],
    ]

    for (const [table, column, extra] of deleteSteps) {
      const query = supabase.from(table).delete().eq(column, gameId)
      if (extra?.eq) {
        query.eq(extra.eq[0], extra.eq[1])
      }
      const { error } = await query
      if (error) {
        console.error(`Error deleting from ${table}:`, error)
        return
      }
    }

    onRefresh()
    alert('Game deleted successfully!')
  }

  return (
    <>
      {showEditModal && (
        <EditGameModal
          game={game}
          userId={userId}
          onGameUpdated={onRefresh}
          showModal={showEditModal}
          setShowModal={setShowEditModal}
        />
      )}

      <div
        className="group w-full max-w-[320px] md:max-w-[340px] lg:max-w-[360px] aspect-[3/4] rounded-[18px] overflow-hidden bg-base-100 shadow-md transition-transform duration-300 ease-in-out hover:-translate-y-1 hover:scale-[1.02] hover:shadow-xl mx-auto cursor-pointer"
        style={{ minWidth: 0 }}
        onClick={() => {
          if (isTouchDevice && !isOverlayOpen) {
            setIsOverlayOpen(true)
            return
          }
          navigate(`/app/game/${game.id}`)
        }}
      >
        <div className="relative h-full w-full">
          <img
            src={coverImage}
            alt={game.title}
            className="absolute inset-0 h-full w-full object-cover"
          />
          <div
            className={`absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-black/70 via-black/35 to-transparent transition-opacity duration-200 ${
              isOverlayOpen ? 'opacity-100' : 'opacity-0'
            } group-hover:opacity-100 group-focus-within:opacity-100`}
          />
          <div
            className={`absolute bottom-3 left-1/2 w-[92%] -translate-x-1/2 rounded-lg bg-black/60 p-3 backdrop-blur-sm transition-opacity duration-200 ${
              isOverlayOpen
                ? 'opacity-100 pointer-events-auto'
                : 'opacity-0 pointer-events-none'
            } group-hover:opacity-100 group-hover:pointer-events-auto group-focus-within:opacity-100 group-focus-within:pointer-events-auto`}
          >
            <div className="flex justify-between items-start gap-2">
              <h2
                className="text-white text-base font-semibold leading-tight flex-1 break-words"
                style={{ textShadow: '0 1px 6px rgba(0,0,0,0.45)' }}
              >
                {game.title}
              </h2>
            </div>

            <div className="mt-2 opacity-0 pointer-events-none transition-opacity duration-200 group-hover:opacity-100 group-hover:pointer-events-auto group-focus-within:opacity-100 group-focus-within:pointer-events-auto">
              <div className="flex justify-end">
                <div
                  className={`badge ${getStatusBadgeColor(
                    game.status || ''
                  )} whitespace-nowrap flex-shrink-0`}
                >
                  {game.status}
                </div>
              </div>

              <div className="flex gap-2 items-center flex-wrap mt-2 text-xs">
                {game.platforms?.map((platform, index) => (
                  <span
                    key={index}
                    className="badge badge-outline border-white/40 text-white/90"
                  >
                    {platform}
                  </span>
                ))}
              </div>

              <div className="flex gap-2 items-center flex-wrap mt-2 mb-3 text-xs">
                {game.genres?.map((genre) => (
                  <span key={genre} className="badge badge-accent">
                    {genre}
                  </span>
                ))}
              </div>

              <div className="w-full bg-white/15 rounded-full overflow-hidden mb-3">
                <div
                  className="bg-primary rounded-full h-2 transition-all duration-300"
                  style={{ width: `${game.progress || 0}%` }}
                />
              </div>

              <div className="card-actions justify-end gap-2">
                <button
                  className="btn btn-xs btn-secondary"
                  onClick={(e) => {
                    e.stopPropagation()
                    setShowEditModal(true)
                  }}
                >
                  Edit
                </button>
                <button
                  className="btn btn-xs btn-error"
                  onClick={(e) => {
                    e.stopPropagation()
                    handleDeleteClick(game.id)
                  }}
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

export default GameCardItem
